import type {
  ActivityCockpitSnapshot,
  CockpitCostTotal,
  CockpitCoverage,
  CockpitSectionStatus,
} from '@internal/spassu-code-contract';
import type { CockpitMetricView } from './cockpitViewModel';
import { usageMetricDetails } from './createCockpitMetricDetails';
import {
  leadTimeMetric,
  unclassifiedTimeMetric,
} from './createCockpitTimeMetricViews';
import { formatCanonicalCost, formatCanonicalInteger } from './formatters';
import { limitationLabel, sectionStatusLabel } from './semanticLabels';
import {
  metricReasonCause,
  metricReasonGuidance,
} from './metricReasonGuidance';
import type { MetricInput } from './metricInput';

export function createCockpitMetricViews(
  snapshot: ActivityCockpitSnapshot,
): CockpitMetricView[] {
  const usage = snapshot.sections.usage.value;
  return [
    leadTimeMetric(snapshot, metric),
    unclassifiedTimeMetric(snapshot, metric),
    usageMetric(
      'input-tokens',
      'Tokens de entrada',
      usage?.inputTokens.value ?? null,
      usage?.inputTokens.coverage ?? 'none',
      snapshot,
    ),
    usageMetric(
      'output-tokens',
      'Tokens de saída',
      usage?.outputTokens.value ?? null,
      usage?.outputTokens.coverage ?? 'none',
      snapshot,
    ),
    ...costMetrics(snapshot),
  ];
}

function costMetrics(snapshot: ActivityCockpitSnapshot): CockpitMetricView[] {
  const section = snapshot.sections.usage;
  const costs = section.value?.costs ?? [];
  if (costs.length > 0) {
    return costs.map((cost: CockpitCostTotal) =>
      metric({
        id: `cost-${cost.currency}`,
        label: `Custo em ${cost.currency}`,
        rawValue: cost.amount,
        displayValue: formatCanonicalCost(cost.amount, cost.currency),
        // the formatted value already shows the currency, and so does the label
        unit: null,
        status: section.status,
        coverage: section.coverage,
        provenance: section.provenance,
        nature: cost.nature,
        explanation: 'Custo reconciliado para a moeda informada.',
        missingReason: 'O custo não foi reconciliado.',
        relation: 'Custos de moedas diferentes permanecem separados.',
        technicalValue: `${cost.amount} ${cost.currency}`,
        primary: false,
        updatedAt: section.updatedAt,
        sourceCutoffAt: snapshot.sourceCutoffAt,
      }),
    );
  }
  // No currency reported: the reason (BR-07) is shown instead of an empty card.
  const reason = section.value?.costReason ?? null;
  if (!reason) return [];
  return [
    metric({
      id: 'cost-unavailable',
      label: 'Custo',
      rawValue: null,
      displayValue: '',
      unit: null,
      status: section.status,
      coverage: section.coverage,
      provenance: section.provenance,
      explanation: 'Custo reconciliado para a moeda informada.',
      missingReason: 'O custo não foi reconciliado.',
      relation: null,
      technicalValue: null,
      primary: false,
      updatedAt: section.updatedAt,
      sourceCutoffAt: snapshot.sourceCutoffAt,
      reasonCode: reason,
    }),
  ];
}

function usageMetric(
  id: string,
  label: string,
  value: number | null,
  coverage: CockpitCoverage,
  snapshot: ActivityCockpitSnapshot,
): CockpitMetricView {
  const section = snapshot.sections.usage;
  const reason =
    id === 'input-tokens'
      ? section.value?.diagnostics?.inputReason ?? null
      : section.value?.diagnostics?.outputReason ?? null;
  return metric({
    id,
    label,
    rawValue: value,
    displayValue: formatCanonicalInteger(value),
    unit: 'tokens',
    status: section.status,
    coverage,
    provenance: section.provenance,
    explanation: 'Uso reconciliado a partir das evidências disponíveis.',
    missingReason: 'O uso não foi reconciliado para esta atividade.',
    relation: 'Entrada e saída são apresentadas separadamente.',
    limitations: section.limitations,
    technicalValue: value === null ? null : String(value),
    primary: true,
    updatedAt: section.updatedAt,
    sourceCutoffAt: snapshot.sourceCutoffAt,
    reasonCode: reason,
    counts: section.value?.diagnostics
      ? {
          observed: section.value.diagnostics.observedEvents,
          reconciled: section.value.diagnostics.reconciledUsages,
          pending: section.value.diagnostics.unreconciledEvents,
          excluded: section.value.diagnostics.excludedEvidence,
          exclusionReasons: section.value.diagnostics.exclusionReasons,
        }
      : null,
    usageObservationState: section.value?.diagnostics?.observationState ?? null,
    stale:
      snapshot.sessionObservability?.stale === true ||
      section.value?.diagnostics?.observationState === 'stale',
    details: usageMetricDetails(
      id,
      section.value,
      snapshot.sections.time.value,
    ),
  });
}

function metric(input: MetricInput): CockpitMetricView {
  const status = metricStatus(input.status, input.coverage, input.rawValue);
  return {
    id: input.id,
    label: input.label,
    rawValue: input.rawValue,
    displayValue:
      input.rawValue === null ? missingValueLabel(status) : input.displayValue,
    unit: input.unit,
    status,
    coverage: input.coverage,
    provenance: input.provenance,
    nature: input.nature ?? null,
    explanation: input.explanation,
    unavailableReason: metricUnavailableReason(input),
    relation: input.relation,
    technicalValue: input.technicalValue,
    primary: input.primary,
    updatedAt: input.updatedAt,
    sourceCutoffAt: input.sourceCutoffAt,
    periodStartedAt: input.periodStartedAt ?? null,
    periodEndedAt: input.periodEndedAt ?? null,
    provisional: input.provisional ?? false,
    proportion: input.proportion ?? null,
    reasonCode:
      input.rawValue === null && input.status === 'failed'
        ? 'section-load-failed'
        : input.reasonCode ?? null,
    ...metricReasonGuidance(
      input.rawValue === null && input.status === 'failed'
        ? 'section-load-failed'
        : input.reasonCode ?? null,
    ),
    counts: input.counts ?? null,
    stale: input.stale ?? false,
    usageObservationState: input.usageObservationState ?? null,
    details: input.details ?? null,
  };
}

function metricUnavailableReason(input: MetricInput): string | null {
  if (input.rawValue !== null) return null;
  if (input.status === 'failed') {
    return 'A seção de métricas não pôde ser atualizada.';
  }
  const cause = metricReasonCause(input.reasonCode ?? null);
  if (cause) return cause;
  const limitation = input.limitations?.[0];
  return limitation ? limitationLabel(limitation) : input.missingReason;
}

function metricStatus(
  status: CockpitSectionStatus,
  coverage: CockpitCoverage,
  value: string | number | null,
): CockpitSectionStatus {
  if (value === null) {
    if (
      status === 'empty' ||
      status === 'failed' ||
      status === 'not-applicable'
    )
      return status;
    return 'unavailable';
  }
  if (status === 'partial' || coverage === 'partial') return 'partial';
  return status === 'empty' || status === 'unavailable' ? 'ready' : status;
}

function missingValueLabel(status: CockpitSectionStatus): string {
  return sectionStatusLabel(status);
}
